/* global DISPLAY_DATE_FORMAT */

import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'

import { authInfoReselector as stateSelector } from 'reduxModules/auth/selectors'
import { accountDispatch as dispatcher } from 'reduxModules/auth/dispatchers'

import Orders from 'components/Account/Orders'
import PaymentMethods from 'components/Account/PaymentMethods'
import Addresses from 'components/Account/Addresses/_lazy'
import Profile from '../Profile/index.jsx'
import DeleteAccount from './DeleteAccount'

import 'rodal/lib/rodal.css'

import './styles.css'
import ManageSubscription from './ManageSubscription/index.jsx'
import RetentionDialog from './RetentionDialog/index.jsx'

export function MyAccount({
  firstName = '',
  lastName = '',
  subscription = null,
  ...props
}) {
  const confirmPayment = () => {
    // couldGiftSubscription users are on a trial and must pay first; canGift
    // users are already eligible and go straight to the gift flow.
    props.dispatchCancelFreeTrial(true, props.canGiftSubscription)
  }
  const canMakeChanges =
    !subscription?.pendingChange && !subscription?.pendingDowngrade
  return (
    <>
      <div className="account-section" id="MyAccount">
        <div className="personal-information">
          <div className="subheading">
            <h3 className="account-section__title heading">My Account</h3>
            {!props.profileIsOpen && (
              <button
                className="btn -mini -blue-2"
                type="button"
                onClick={() => props.openProfileSection()}
                style={{ height: 20 }}
              >
                Edit
              </button>
            )}
          </div>
          {!props.profileIsOpen ? (
            <div className="grid">
              <div className="first-name">
                <span>first name</span>
                <span>{firstName}</span>
              </div>
              <div className="last-name">
                <span>last name</span>
                <span>{lastName}</span>
              </div>
              <div className="email">
                <span>email</span>
                <span>{props.email}</span>
              </div>
            </div>
          ) : (
            <Profile cancel={props.closeProfileSection} />
          )}
          <Link
            to="/account/change_password"
            className="change-password"
            style={{ display: !props.createdByProxy ? 'block' : 'none' }}
          >
            Change Password
          </Link>
          {subscription && !props.createdByProxy && (
            <>
              <h3 className="account-section__title">Subscription</h3>
              <div className="subscription-info">
                <div className="left-section">
                  <div className="description">
                    <i className={`icon-monster-${subscription.rank}`} />
                    <div className="plan-information">
                      <span className="plan-name">
                        {subscription.plan === 'Lifetime' && (
                          <span aria-hidden="true" className="lifetime-star">
                            ★
                          </span>
                        )}
                        {subscription.plan}
                      </span>
                      {subscription.nextBillingDate &&
                        !subscription.free &&
                        !subscription?.pendingChange && (
                          <span className="billing-date">
                            Billing Date:{' '}
                            {dayjs(subscription.nextBillingDate).format(
                              DISPLAY_DATE_FORMAT
                            )}
                          </span>
                        )}
                      {canMakeChanges && (
                        <ManageSubscription
                          cancel={() =>
                            props.dispatchCancelSubscription(subscription)
                          }
                          subscription={subscription}
                          downgrade={props.downgradeSubscription}
                          isMobile={props.isMobile}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="right-section">
                  {subscription?.rank < 3 && !subscription.free && (
                    <Link to="/change_subscription">Upgrade</Link>
                  )}
                  {subscription?.pendingChange &&
                    !subscription?.pendingDowngrade && (
                      <div className="pending-message">
                        <span className="message">
                          Your subscription cancellation is pending and will
                          take effect on
                        </span>
                        <span
                          className="date"
                          style={{
                            display: subscription.nextBillingDate
                              ? 'inline-block'
                              : 'none'
                          }}
                        >
                          {' '}
                          {dayjs(subscription.nextBillingDate).format(
                            DISPLAY_DATE_FORMAT
                          )}
                        </span>
                      </div>
                    )}
                  {subscription?.pendingDowngrade &&
                    subscription?.pendingChange && (
                      <div className="pending-message">
                        <span className="message">
                          Your subscription downgrade is pending and will take
                          effect on
                        </span>
                        <span
                          className="date"
                          style={{
                            display: subscription.nextBillingDate
                              ? 'inline-block'
                              : 'none'
                          }}
                        >
                          {' '}
                          {dayjs(subscription.nextBillingDate).format(
                            DISPLAY_DATE_FORMAT
                          )}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </>
          )}
          {(props.couldGiftSubscription || props.canGiftSubscription) && (
            <div className="subscription-gift-notice">
              {props.couldGiftSubscription && (
                <p className="subscription-gift-notice__text">
                  Pay for your current subscription and give a free matching
                  subscription to a family member or friend.
                </p>
              )}
              {props.canGiftSubscription && (
                <p className="subscription-gift-notice__text">
                  Congratulations! You are eligible to give a free matching
                  subscription to a family member or friend.
                </p>
              )}
              <button
                className="subscription-gift-notice__cta btn -wide -orange"
                onClick={confirmPayment}
                type="button"
              >
                Gift Matching Subscription
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="account-section">
        <PaymentMethods />
      </div>
      <div className="account-section">
        <Addresses />
      </div>
      <div className="account-section">
        <Orders />
      </div>
      <div className="account-section">
        <DeleteAccount isMobile={props.isMobile} />
      </div>
      <RetentionDialog
        open={props.retentionDialogIsOpen}
        onClose={props.closeRetentionDialog}
        subscription={subscription}
        acceptRetentionDiscount={props.acceptRetentionDiscount}
        isMobile={props.isMobile}
        isDisabled={props.retentionDialogIsDisabled}
        cancel={() => props.dispatchDirectCancelSubscription()}
      />
    </>
  )
}

MyAccount.propTypes = {
  couldGiftSubscription: PropTypes.bool.isRequired,
  email: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  subscription: PropTypes.shape({
    plan: PropTypes.string.isRequired,
    free: PropTypes.bool.isRequired,
    rank: PropTypes.number.isRequired
  }),
  dispatchCancelSubscription: PropTypes.func.isRequired
}

MyAccount.defaultProps = {
  firstName: '',
  lastName: '',
  subscription: null
}

export default connect(stateSelector, dispatcher)(MyAccount)
